/**
 * In-memory cache для linked_record card-resolution.
 *
 * Компоненти запитують `resolveCard(slug, id)` (rich bubble) або `resolveLabel`
 * (лише title) — функція batches IDs у короткий interval (50ms) і виконує один
 * HTTP GET /api/lookup/:slug?ids=… на пачку, повертає проміс що резолвиться з
 * RelationCard.
 *
 * Cache live until page reload — для типових сесій це economy (один запис
 * компанії на 10+ Person'ах → 1 fetch). Інвалідація — invalidateLookupCache().
 */

import { resolveRelationCards, type RelationCard } from '../data-tables-api';

export type { RelationCard };

/** Картка-плейсхолдер для not-found / помилки (tombstone). */
function tombstone(slug: string, id: string): RelationCard {
  const short = `${id.slice(0, 8)}…`;
  return {
    id,
    title: short,
    subtitle: null,
    email: null,
    phone: null,
    telegram: null,
    type: slug,
    initials: '—',
    label: short,
    deleted: true,
  };
}

/** Map `${slug}/${id}` → RelationCard. */
const cache = new Map<string, RelationCard>();
/** Pending queue per slug — для batching у наступний tick. */
const pending = new Map<
  string,
  { ids: Set<string>; resolvers: Map<string, Array<(card: RelationCard) => void>> }
>();
/** Активний таймер per slug. */
const timers = new Map<string, ReturnType<typeof setTimeout>>();

const BATCH_INTERVAL_MS = 50;

export function resolveCard(slug: string, id: string): Promise<RelationCard> {
  const key = `${slug}/${id}`;
  const cached = cache.get(key);
  if (cached !== undefined) return Promise.resolve(cached);

  return new Promise<RelationCard>((resolve) => {
    let q = pending.get(slug);
    if (!q) {
      q = { ids: new Set(), resolvers: new Map() };
      pending.set(slug, q);
    }
    q.ids.add(id);
    const arr = q.resolvers.get(id) ?? [];
    arr.push(resolve);
    q.resolvers.set(id, arr);

    // Re-arm timer для цього slug.
    const existing = timers.get(slug);
    if (existing) clearTimeout(existing);
    timers.set(slug, setTimeout(() => void flush(slug), BATCH_INTERVAL_MS));
  });
}

/** Зворотна сумісність: лише title. */
export async function resolveLabel(slug: string, id: string): Promise<string> {
  const c = await resolveCard(slug, id);
  // Tombstone (not found) → '' щоб старі компоненти показали fallback-моно-id.
  return c.deleted ? '' : c.title;
}

async function flush(slug: string): Promise<void> {
  timers.delete(slug);
  const q = pending.get(slug);
  if (!q || q.ids.size === 0) return;
  pending.delete(slug);

  const ids = Array.from(q.ids);
  try {
    const r = await resolveRelationCards(slug, ids);
    const found = new Map(r.items.map((it) => [it.id, it]));
    for (const id of ids) {
      const cardVal = found.get(id) ?? tombstone(slug, id);
      cache.set(`${slug}/${id}`, cardVal);
      for (const fn of q.resolvers.get(id) ?? []) fn(cardVal);
    }
  } catch {
    // Не пишемо у cache — наступна спроба resolve буде fetch-ити знов.
    for (const id of ids) {
      const ts = tombstone(slug, id);
      for (const fn of q.resolvers.get(id) ?? []) fn(ts);
    }
  }
}

/** Записати картку у кеш напряму (наприклад після inline-create у picker'і). */
export function primeCard(slug: string, c: RelationCard): void {
  cache.set(`${slug}/${c.id}`, c);
}

/** Invalidate cache (наприклад після CRUD у Settings → Структура даних). */
export function invalidateLookupCache(slug?: string): void {
  if (!slug) {
    cache.clear();
    return;
  }
  const prefix = `${slug}/`;
  for (const k of cache.keys()) {
    if (k.startsWith(prefix)) cache.delete(k);
  }
}
